/**
 * Ownership Review overlay wiring (Loop #1148).
 *
 * The queue rows carry `data-ownership-review-url`. Clicking one hands the URL to
 * `rfModal` (static/js/src/modal-history.js), which renders the SeeMe overlay and keeps
 * the canonical `?modal=` parameter in the address bar so the review survives a refresh,
 * a copied link, and Back/Forward. A Waiting row points at the live-preview endpoint
 * (`ownership-review/waiting/<property_pk>/modal/`) instead of a saved row's; both are
 * the same overlay and take the same chrome.
 *
 * The URL is the same on every device; only the chrome differs. A phone gets a bottom
 * sheet, requested with `overlay=sheet` on the content request alone, so the URL someone
 * copies off a phone still opens the desktop modal.
 */

(function () {
    'use strict';

    var MODAL_PATH = /^\/acqs\/ownership-review\/(?:waiting\/)?\d+\/modal\/(?:\?|$)/;

    function isMobile() {
        return !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
    }

    function overlayOptions(historyMode) {
        var mobile = isMobile();
        return {
            type: mobile ? 'sheet' : 'modal',
            overlayClass: mobile ? '' : 'modal--fullscreen',
            requestParams: mobile ? {overlay: 'sheet'} : null,
            history: historyMode || 'push',
        };
    }

    function openReview(url, historyMode) {
        if (!url || !window.rfModal) return;
        var mode = historyMode || (window.rfModal.active() ? 'replace' : 'push');
        window.rfModal.open(url, overlayOptions(mode));
    }

    // A direct link, a refresh, or a history traversal knows only the canonical URL.
    // Pick the chrome for the current viewport before the adapter loads it.
    document.addEventListener('rfmodal:resolve', function (event) {
        var detail = event.detail;
        if (!detail || !MODAL_PATH.test(detail.url || '')) return;
        detail.options = overlayOptions('none');
    });

    // SeeMe adopts a lazy overlay with `htmx.process()` alone — no `htmx:afterSettle` —
    // so auto-resize.js never sees the footer's one-line note box. Bind it here. (A 422
    // footer re-render is an ordinary HTMX swap and auto-resize.js handles that itself.)
    document.addEventListener('seeme:overlay-loaded', function (event) {
        var el = event.target;
        if (!el || !el.hasAttribute || !el.hasAttribute('data-ownership-review-overlay')) return;
        if (!window._cc || !window._cc.initAutoResize) return;
        el.querySelectorAll('[data-autoresize]').forEach(window._cc.initAutoResize);
    });

    // Strip `?modal=` ourselves before refetching the queue. `rfModal.close()` also
    // clears it, but from the overlay's close event, and modal-history's own
    // `htmx:afterSettle` handler re-reads the address bar as soon as #page-content
    // settles — a stale `modal=` there would reopen the overlay we just answered from.
    function stripModalParam() {
        var params = new URLSearchParams(window.location.search);
        if (!params.has('modal')) return;
        params.delete('modal');
        var query = params.toString();
        var state = history.state ? Object.assign({}, history.state) : {};
        history.replaceState(state, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    }

    // An answered row leaves the Needs-decision list and lands in Done, and all three tab
    // counts move with it. Refetching the page is the same swap the tab strip makes, so
    // the list, the counts and the pager stay one render.
    document.body.addEventListener('ownershipReviewAnswered', function () {
        stripModalParam();
        if (window.rfModal) window.rfModal.close();
        if (!window.htmx || !document.getElementById('page-content')) return;
        window.htmx.ajax('GET', window.location.pathname + window.location.search, {
            source: document.body,
            target: '#page-content',
            select: '#page-content',
            swap: 'outerHTML',
        });
    });

    document.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var trigger = target.closest('[data-ownership-review-url]');
        if (!trigger) return;
        // The house icon beside a row's address is a `data-sheet` link to the property
        // sheet. On a phone it sits inside the row that opens the review, so let sheets.js
        // have that click.
        var sheetLink = target.closest('[data-sheet]');
        if (sheetLink && trigger.contains(sheetLink)) return;
        event.preventDefault();
        event.stopPropagation();
        openReview(trigger.getAttribute('data-ownership-review-url'));
    });
})();
